import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faKey } from "@fortawesome/free-solid-svg-icons";
import Home from "./Home";
import TemporaryActivationCode from "./TemporaryActivationCode";
import { getAppearancePreference } from "./plansAppearance";
import { WINDOWS_INSTALLER_URL } from "./installerDownloads";
import "./Downloads.css";

const downloads = [
  { platform: "Windows", url: WINDOWS_INSTALLER_URL },
  { platform: "macOS", url: "https://signaltuner.com/update/macos/SignalTuner.dmg" },
];

function PlatformIcon({ platform }) {
  return platform === "Windows"
    ? <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M4 10 29 6v25H4zm30-5 26-4v30H34zM4 35h25v25L4 56zm30 0h26v28l-26-4z" fill="currentColor" /></svg>
    : <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M17.05 12.54c-.02-2.1 1.72-3.12 1.8-3.17-.98-1.42-2.5-1.62-3.04-1.64-1.29-.14-2.53.77-3.18.77-.65 0-1.65-.75-2.72-.72-1.4.02-2.7.82-3.42 2.07-1.46 2.53-.38 6.27 1.04 8.32.7 1 1.53 2.12 2.6 2.08 1.04-.04 1.43-.67 2.68-.67 1.25 0 1.6.67 2.72.65 1.13-.02 1.84-1.02 2.53-2.02.8-1.15 1.13-2.26 1.15-2.31-.03-.01-2.14-.82-2.16-3.36ZM14.97 6.38c.58-.7.98-1.67.87-2.65-.85.04-1.88.57-2.48 1.27-.55.63-1.03 1.62-.9 2.58.95.08 1.93-.48 2.51-1.2Z" /></svg>;
}

export default function Downloads({ loggedIn, onLogin, onForgotPassword, onCreateAccount }) {
  const [showLogin, setShowLogin] = useState(false);
  const [information, setInformation] = useState(null);
  return <main className={`downloads-page downloads-page--${getAppearancePreference()}`}>
    <div className="downloads-inner">
      <div className="downloads-layout">
        <div className="downloads-actions">
          <section className="downloads-header" aria-labelledby="downloads-heading">
            <p className="downloads-eyebrow">SIGNALTUNER DESKTOP</p>
            <h1 id="downloads-heading">Better connections,</h1>
            <h1 id="downloads-heading">Better presence,</h1>
            <h1 id="downloads-heading">Better meetings.</h1>
            <p className="downloads-intro">Download the SignalTuner desktop app and get actionable insights into your connectivity health anytime.</p>
            <p className="downloads-intro">Share data with your team to identify friction points in your streaming video meetings. </p>
            <p className="downloads-intro">Download for free. No credit card required. </p>
          </section>
          <div className="downloads-platforms">
            <div className="downloads-activation-heading downloads-platforms-heading">
              <FontAwesomeIcon className="downloads-key-icon" icon={faDownload} aria-hidden="true" />
              <h2 className="downloads-card-label">DOWNLOAD NOW</h2>
            </div>
            {downloads.map(({ platform, url }) => <div className="downloads-platform" key={platform}>
              <a className="downloads-primary-button" href={url}>
                <span className={`downloads-platform-icon${platform === "macOS" ? " downloads-platform-icon--macos" : ""}`}><PlatformIcon platform={platform} /></span>
                Download for {platform}
              </a>
            </div>)}
          </div>
          <section className="downloads-activation" aria-labelledby="downloads-activation-heading">
            <div className="downloads-activation-heading">
              <FontAwesomeIcon className="downloads-key-icon" icon={faKey} aria-hidden="true" />
              <h2 id="downloads-activation-heading" className="downloads-card-label">DEVICE ACTIVATION</h2>
            </div>
            <div className="downloads-activation-control">
              {loggedIn ? <TemporaryActivationCode compact /> :
                <>
                  <button className="downloads-primary-button" type="button" aria-label="Sign in to generate activation code" onClick={() => setShowLogin(true)}>Generate activation code</button>
                  <div className="temporaryActivationCodeField" aria-label="Activation code"><span className="temporaryActivationCodePlaceholder" aria-hidden="true">******</span></div>
                </>}
            </div>
          </section>
        </div>
        <section className="downloads-card downloads-preview" aria-labelledby="downloads-preview-heading">
          <div className="downloads-preview-header">
            <span className="downloads-preview-logo"><img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="" /></span>
            <h2 id="downloads-preview-heading">SignalTuner desktop app</h2>
            <span className="downloads-preview-badge">APP PREVIEW</span>
          </div>
          <img className="downloads-preview-image" src={`${process.env.PUBLIC_URL}/desktop-app-preview.png`}
            alt="SignalTuner desktop app showing connectivity health analysis, signal score, and network metrics" />
        </section>
      </div>
      <aside className="downloads-information">
        <div>
          <h2>Downloads and activation codes are linked to your SignalTuner account</h2>
          <p>Need help? Visit <button className="downloads-inline-link" type="button" onClick={() => setInformation("setup")}>setup instructions</button> or contact support.</p>
        </div>
        <div className="downloads-information-actions">
          <button className="downloads-outline-button" type="button" onClick={() => setInformation("release")}>Release notes</button>
          <Link className="downloads-outline-button" to="/support">Contact support</Link>
        </div>
      </aside>
    </div>
    {showLogin && !loggedIn && <div className="downloads-dialog-backdrop" onMouseDown={event => {
      if (event.target === event.currentTarget) setShowLogin(false);
    }}>
      <div className="downloads-login-dialog" role="dialog" aria-modal="true" aria-label="Sign in to generate an activation code"
        onKeyDown={event => { if (event.key === "Escape") setShowLogin(false); }}>
        <Home loginOnly loggedIn={false} showLoginForm loginClick={onLogin}
          showForgotPasswordClick={onForgotPassword} showUserCreationClick={onCreateAccount}
          hideHomePageFormsClick={() => setShowLogin(false)} />
      </div>
    </div>}
    {information && <div className="downloads-dialog-backdrop" onMouseDown={event => {
      if (event.target === event.currentTarget) setInformation(null);
    }}>
      <section className="downloads-notes-dialog" role="dialog" aria-modal="true" aria-labelledby="downloads-notes-heading"
        onKeyDown={event => { if (event.key === "Escape") setInformation(null); }}>
        <button className="downloads-notes-close" type="button" onClick={() => setInformation(null)} aria-label="Close information">&times;</button>
        {information === "release" ? <>
          <p className="downloads-card-label">DESKTOP RELEASE NOTES</p>
          <h2 id="downloads-notes-heading">Desktop activation</h2>
          <p>The current desktop activation flow uses a five-minute, single-use code generated for your account. Generate a new code here if the previous one expires or has already been used.</p>
        </> : <>
          <p className="downloads-card-label">SETUP INSTRUCTIONS</p>
          <h2 id="downloads-notes-heading">Connect your device</h2>
          <p>Download the desktop client for your operating system, install and open it, then sign in here to generate an activation code. Enter that code in the desktop app before it expires.</p>
        </>}
      </section>
    </div>}
  </main>;
}
